// SocialMovementSection.jsx
// Bottom-of-landing BIG BOLD social movement wall.
// Inaugural entry: EAT THE LANDLORDS · dictated by the Founder 2026-02-13.
// P.S. Public submission door for anyone else's movement.
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SAGE = `${BACKEND_URL}/api/sage`;

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "$0";
  const v = Number(n);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtMoneyCents(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "$0.00";
  const v = Number(n);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function daysUntil(iso) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const days = Math.max(0, Math.floor((then - now) / (1000 * 60 * 60 * 24)));
    return days;
  } catch { return null; }
}

// ────────────────────────────────────────────────────────────────
// EAT THE LANDLORDS calculator
// ────────────────────────────────────────────────────────────────
function EatTheLandlordsCalculator({ onResult }) {
  const [years, setYears] = useState("");
  const [rent, setRent] = useState("");
  const [market, setMarket] = useState("");
  const [term, setTerm] = useState("20");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  async function calc() {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const res = await fetch(`${SAGE}/movements/eat-the-landlords/calculate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          years_lived: Number(years) || 0,
          monthly_rent_usd: Number(rent) || 0,
          fair_market_value_usd: Number(market) || 0,
          term_years: Number(term) || 20,
        }),
      });
      if (!res.ok) {
        let msg = "";
        try { msg = (await res.json()).detail || ""; } catch {}
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      if (onResult) onResult(data);
    } catch (e) {
      setErr(e.message || "calculation failed");
    } finally {
      setBusy(false);
    }
  }

  const canRun = Number(years) > 0 && Number(rent) > 0 && Number(market) > 0;

  return (
    <div className="border-2 border-sage-accent bg-sage-bg p-6 md:p-8" data-testid="etl-calculator">
      <div className="text-[11px] uppercase tracking-[0.3em] text-sage-accent mb-4 font-bold">
        Run the numbers on your own tenancy
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Years you've lived there</div>
          <input
            data-testid="etl-years"
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            placeholder="e.g. 7"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-lg text-sage-text focus:border-sage-accent outline-none"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Monthly rent paid ($)</div>
          <input
            data-testid="etl-rent"
            type="number"
            step="10"
            min="0"
            value={rent}
            onChange={(e) => setRent(e.target.value)}
            placeholder="e.g. 1600"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-lg text-sage-text focus:border-sage-accent outline-none"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Fair market value of the home ($)</div>
          <input
            data-testid="etl-market"
            type="number"
            step="1000"
            min="0"
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            placeholder="e.g. 280000"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-lg text-sage-text focus:border-sage-accent outline-none"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Repayment term (years · zero interest)</div>
          <input
            data-testid="etl-term"
            type="number"
            step="1"
            min="1"
            max="50"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="20"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-lg text-sage-text focus:border-sage-accent outline-none"
          />
        </label>
      </div>

      <button
        onClick={calc}
        disabled={busy || !canRun}
        data-testid="etl-calculate"
        className="w-full md:w-auto border-2 border-sage-accent bg-sage-accent text-sage-bg px-8 py-3 text-sm uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
      >
        {busy ? "Running the numbers…" : "Run the numbers"}
      </button>

      {err && (
        <div className="mt-4 text-sm text-red-700" data-testid="etl-error">{err}</div>
      )}

      {result && (
        <div className="mt-6 border-t-2 border-sage-accent pt-6 space-y-4" data-testid="etl-result">
          <div className="grid md:grid-cols-3 gap-4">
            <ResultTile label="Total rent you've paid" value={fmtMoney(result.total_rent_paid_usd)} tone="normal" testid="etl-total-rent" />
            <ResultTile label="Remainder to buy the house" value={fmtMoney(result.remainder_to_market_usd)} tone="normal" testid="etl-remainder" />
            <ResultTile label="Our offer (10% more · zero interest)" value={fmtMoney(result.our_offer_usd)} tone="loud" testid="etl-offer" />
          </div>

          <div className="border border-sage-border p-4 bg-sage-pale/30">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Monthly payment on our offer</div>
            <div className="text-3xl md:text-4xl font-bold text-sage-text tracking-tight" data-testid="etl-monthly">
              {fmtMoneyCents(result.monthly_payment_usd)} / month
            </div>
            <div className="text-xs text-sage-muted mt-1">
              {result.inputs.term_years} years · 0% interest · you own the deed on the last check
            </div>
          </div>

          {result.already_paid_off && (
            <div className="border-2 border-red-700 bg-red-50 p-4" data-testid="etl-paid-off">
              <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 mb-2 font-bold">
                You have already overpaid
              </div>
              <div className="text-lg text-red-900 sage-prose">
                You've paid <strong>{fmtMoney(result.overpaid_by_usd)}</strong> more than the house is worth. The landlord is done. You keep the house.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultTile({ label, value, tone, testid }) {
  const cls = tone === "loud"
    ? "border-2 border-sage-accent bg-sage-accent text-sage-bg p-4"
    : "border border-sage-border p-4";
  return (
    <div className={cls} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${tone === "loud" ? "opacity-80" : "text-sage-muted"}`}>
        {label}
      </div>
      <div className="text-2xl md:text-3xl font-bold tracking-tight">
        {value}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Movement submission form
// ────────────────────────────────────────────────────────────────
function MovementSubmitForm() {
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const canSubmit =
    name.trim().length >= 3 &&
    tagline.trim().length >= 5 &&
    description.trim().length >= 30 &&
    /.+@.+\..+/.test(email);

  async function submit() {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const res = await fetch(`${SAGE}/movements/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          movement_name: name.trim(),
          tagline: tagline.trim(),
          description: description.trim(),
          email: email.trim().toLowerCase(),
          url: url.trim(),
        }),
      });
      if (!res.ok) {
        let msg = "";
        try { msg = (await res.json()).detail || ""; } catch {}
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setName(""); setTagline(""); setDescription(""); setEmail(""); setUrl("");
    } catch (e) {
      setErr(e.message || "submission failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-sage-border bg-sage-bg p-6" data-testid="movement-submit-form">
      <div className="text-[11px] uppercase tracking-[0.3em] text-sage-accent mb-1 font-bold">
        Bring your movement
      </div>
      <div className="text-sm text-sage-muted sage-prose mb-4">
        Baker & Sage reviews every submission before it goes on the public wall. Two per email, per day.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Movement name</div>
          <input
            data-testid="movement-name"
            type="text"
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. FEED THE BOOKSTORES"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Contact email</div>
          <input
            data-testid="movement-email"
            type="email"
            maxLength={200}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@wherever.com"
            className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
          />
        </label>
      </div>

      <label className="block mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Tagline (one line)</div>
        <input
          data-testid="movement-tagline"
          type="text"
          maxLength={300}
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="What this movement does in one sentence"
          className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
        />
      </label>

      <label className="block mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Description (30-8000 chars)</div>
        <textarea
          data-testid="movement-description"
          maxLength={8000}
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Who does it help. Who does it aim at. What is the deed."
          className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none resize-none"
        />
        <div className="text-[10px] text-sage-muted mt-1">{description.length} / 8000</div>
      </label>

      <label className="block mt-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Link (optional)</div>
        <input
          data-testid="movement-url"
          type="url"
          maxLength={500}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
        />
      </label>

      <button
        onClick={submit}
        disabled={busy || !canSubmit}
        data-testid="movement-submit"
        className="mt-6 w-full md:w-auto border-2 border-sage-accent bg-sage-accent text-sage-bg px-8 py-3 text-sm uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
      >
        {busy ? "Sending…" : "Send it to Baker & Sage"}
      </button>

      {err && <div className="mt-4 text-sm text-red-700" data-testid="movement-error">{err}</div>}
      {result && (
        <div className="mt-4 border-2 border-sage-accent bg-sage-pale/40 p-4" data-testid="movement-success">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1 font-bold">Received</div>
          <div className="text-sm text-sage-text sage-prose">{result.message}</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Main section
// ────────────────────────────────────────────────────────────────
export default function SocialMovementSection() {
  const [stats, setStats] = useState(null);
  const [approved, setApproved] = useState([]);

  async function loadStats() {
    try {
      const res = await fetch(`${SAGE}/movements/eat-the-landlords/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }
  async function loadApproved() {
    try {
      const res = await fetch(`${SAGE}/movements`);
      if (res.ok) {
        const data = await res.json();
        setApproved(data.movements || []);
      }
    } catch {}
  }

  useEffect(() => {
    loadStats();
    loadApproved();
  }, []);

  const days = stats ? daysUntil(stats.deadline_iso) : null;

  return (
    <section
      className="border-t-4 border-sage-accent bg-sage-text text-sage-bg py-16 md:py-24 sage-grain"
      data-testid="social-movement-section"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-8">

        {/* HEADER */}
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-sage-accent mb-3 font-bold">
            Social Movements · A budding new category
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-none" data-testid="social-movement-headline">
            The Brave New World
          </h2>
          <p className="mt-4 text-base md:text-lg opacity-80 sage-prose max-w-3xl">
            Baker & Sage leads the way with the first. Public submissions welcome below.
          </p>
        </div>

        {/* INAUGURAL — EAT THE LANDLORDS */}
        <div className="border-2 border-sage-accent bg-sage-bg text-sage-text p-6 md:p-10" data-testid="eat-the-landlords-block">
          <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-2 font-bold">
                Inaugural movement · Led by Baker & Sage
              </div>
              <h3 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase text-sage-text leading-none" data-testid="etl-title">
                Eat the Landlords
              </h3>
            </div>
            {days !== null && (
              <div className="border border-sage-accent px-4 py-2" data-testid="etl-countdown">
                <div className="text-[9px] uppercase tracking-[0.2em] text-sage-muted">Deadline · Sept 11, 2027</div>
                <div className="text-2xl md:text-3xl font-bold text-sage-accent tracking-tight">
                  {days.toLocaleString("en-US")} days
                </div>
              </div>
            )}
          </div>

          <div className="text-base md:text-lg text-sage-text sage-prose leading-relaxed mb-6 max-w-4xl">
            How long you've lived there. How much rent you paid. How much that
            equals in purchase power of the people. Here is the rest that would
            buy the house at reasonable market prices. Here is our offer —
            it's <strong className="text-sage-accent">10% more</strong>, paid
            on a monthly basis, with <strong className="text-sage-accent">ZERO interest</strong>.
          </div>

          <EatTheLandlordsCalculator onResult={() => loadStats()} />

          {/* Ultimatum */}
          <div className="mt-8 border-2 border-red-700 bg-red-50 p-6" data-testid="etl-ultimatum">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-700 mb-2 font-bold">
              The line
            </div>
            <div className="text-lg md:text-xl text-red-900 sage-prose leading-relaxed font-bold">
              Should you not accept our offer, we will join the worldwide
              movement to stop paying rent, all at once, worldwide, on
              <span className="whitespace-nowrap"> September 11th, 2027</span>.
            </div>
          </div>

          {/* Founder signature */}
          <div className="mt-6 text-right">
            <div className="text-sm text-sage-muted italic sage-prose" data-testid="etl-signature">
              This one was all me.
            </div>
            <div className="text-lg font-bold text-sage-text mt-1">
              — The Founder
            </div>
          </div>

          {/* Live stats */}
          {stats && stats.tenants_calculated > 0 && (
            <div className="mt-8 border-t border-sage-border pt-6 grid md:grid-cols-3 gap-4" data-testid="etl-stats">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Tenants who ran the numbers</div>
                <div className="text-3xl font-bold text-sage-text">{stats.tenants_calculated.toLocaleString("en-US")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Rent extracted from working humans</div>
                <div className="text-3xl font-bold text-sage-text">{fmtMoney(stats.total_rent_extracted_usd)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Total offered by Baker &amp; Sage</div>
                <div className="text-3xl font-bold text-sage-accent">{fmtMoney(stats.total_offered_by_sage_usd)}</div>
              </div>
            </div>
          )}
        </div>

        {/* P.S. + SUBMISSION */}
        <div className="mt-12">
          <div className="text-[11px] uppercase tracking-[0.3em] text-sage-accent mb-2 font-bold">
            P.S. from the Founder
          </div>
          <div className="text-xl md:text-2xl font-bold sage-prose leading-relaxed mb-6 max-w-4xl" data-testid="brave-new-world-ps">
            Bring your social movement / experiment here. This is a budding new category.
            <span className="text-sage-accent"> Welcome to the Brave New World.</span>
          </div>

          <MovementSubmitForm />
        </div>

        {/* APPROVED WALL */}
        {approved.length > 0 && (
          <div className="mt-12" data-testid="movements-approved-wall">
            <div className="text-[11px] uppercase tracking-[0.3em] text-sage-accent mb-4 font-bold">
              Movements on the wall · {approved.length}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {approved.map((m) => (
                <div key={m.movement_id} className="border border-sage-accent bg-sage-bg text-sage-text p-5" data-testid={`movement-${m.movement_id}`}>
                  <div className="text-xl md:text-2xl font-black uppercase tracking-tight mb-1">{m.movement_name}</div>
                  <div className="text-sm text-sage-accent mb-3 sage-prose">{m.tagline}</div>
                  <div className="text-sm text-sage-muted sage-prose whitespace-pre-wrap">{m.description}</div>
                  {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-[10px] uppercase tracking-[0.2em] text-sage-accent border border-sage-accent px-3 py-1 hover:bg-sage-accent hover:text-sage-bg">
                      Open →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
